import React, { useEffect } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Table,
  Card,
  Space,
  Divider,
  Button,
} from "antd";
import { CheckCircleOutlined, StopOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { usePayments } from "../../../hooks/finance/usePayments";

const PaymentDetailsDrawer = ({
  visible,
  onClose,
  paymentId,
  onValidate,
  onVoid,
  canVoid = false,
}) => {
  const { paymentDetails, loading, fetchPaymentDetails } = usePayments();

  useEffect(() => {
    if (visible && paymentId) {
      fetchPaymentDetails(paymentId);
    }
  }, [visible, paymentId]);

  const getStatusColor = (status) => {
    const statusColors = {
      Valid: "green",
      "Pending Validation": "orange",
      Suspense: "red",
      Invalid: "volcano",
      Voided: "default",
    };
    return statusColors[status] || "default";
  };

  const allocationColumns = [
    {
      title: "Fee Assignment",
      dataIndex: ["feeAssignment", "feeName"],
      key: "feeName",
    },
    {
      title: "Amount Allocated",
      dataIndex: "amountAllocated",
      key: "amountAllocated",
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600 }}>
          {parseFloat(amount).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Allocation Date",
      dataIndex: "allocationDate",
      key: "allocationDate",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY HH:mm") : "-"),
    },
    {
      title: "Allocated By",
      dataIndex: "allocatedBy",
      key: "allocatedBy",
      render: (by) => by || <Tag color="cyan">Auto-Allocated</Tag>,
    },
  ];

  if (!paymentDetails && !loading) {
    return null;
  }

  const verifiedStudentName =
    paymentDetails?.studentName || paymentDetails?.student?.name;
  const reportedStudentName = paymentDetails?.reportedStudentName;

  return (
    <Drawer
      title="Payment Details"
      placement="right"
      onClose={onClose}
      open={visible}
      width={720}
      extra={
        paymentDetails && (
          <Space>
            {(paymentDetails.status === "Pending Validation" ||
              paymentDetails.status === "Suspense") && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  onValidate(paymentDetails);
                  onClose();
                }}
              >
                Validate
              </Button>
            )}
            {canVoid &&
              paymentDetails.isManualPayment &&
              paymentDetails.status !== "Voided" && (
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={() => onVoid(paymentDetails)}
                >
                  Void
                </Button>
              )}
          </Space>
        )
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        paymentDetails && (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Basic Payment Info */}
            <Card title="Payment Information" size="small">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Payment Reference">
                  <strong>{paymentDetails.paymentReference}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Student">
                  <Space direction="vertical" size={2}>
                    <span>
                      {verifiedStudentName || reportedStudentName || "-"}
                      {!verifiedStudentName && reportedStudentName && (
                        <Tag color="orange" style={{ marginLeft: 6 }}>
                          Unmatched · SurePay provided
                        </Tag>
                      )}
                    </span>
                    {!verifiedStudentName &&
                      paymentDetails.surePayStudentCode && (
                        <span style={{ color: "#8c8c8c" }}>
                          SurePay code: {paymentDetails.surePayStudentCode}
                        </span>
                      )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Amount">
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#1890ff",
                    }}
                  >
                    {parseFloat(paymentDetails.amount).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Date">
                  {paymentDetails.paymentDate
                    ? dayjs(paymentDetails.paymentDate).format(
                        "DD MMM YYYY HH:mm",
                      )
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method">
                  <Tag>{paymentDetails.paymentMethod}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Channel">
                  {paymentDetails.paymentChannel || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(paymentDetails.status)}>
                    {paymentDetails.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Validation Status">
                  {paymentDetails.validationStatus || "-"}
                </Descriptions.Item>
                {paymentDetails.status === "Voided" && (
                  <>
                    <Descriptions.Item label="Voided By">
                      {paymentDetails.voidedBy || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Voided At">
                      {paymentDetails.voidedAt
                        ? dayjs(paymentDetails.voidedAt).format(
                            "DD MMM YYYY HH:mm",
                          )
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Void Reason">
                      {paymentDetails.voidReason || "-"}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            {/* Gateway Transaction Details */}
            {paymentDetails.gatewayTransaction && (
              <Card title="Gateway Transaction" size="small">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Gateway">
                    {paymentDetails.gatewayTransaction.gatewayName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Order ID">
                    {paymentDetails.gatewayTransaction.gatewayOrderId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color="green">
                      {paymentDetails.gatewayTransaction.gatewayStatus}
                    </Tag>
                  </Descriptions.Item>
                  {paymentDetails.gatewayTransaction.ipnReceivedAt && (
                    <Descriptions.Item label="IPN Received">
                      {dayjs(
                        paymentDetails.gatewayTransaction.ipnReceivedAt,
                      ).format("DD MMM YYYY HH:mm")}
                    </Descriptions.Item>
                  )}
                  {paymentDetails.gatewayTransaction.callbackReceivedAt && (
                    <Descriptions.Item label="Callback Received">
                      {dayjs(
                        paymentDetails.gatewayTransaction.callbackReceivedAt,
                      ).format("DD MMM YYYY HH:mm")}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Webhook Data */}
            {paymentDetails.webhookData && (
              <Card title="Webhook Data" size="small">
                <Descriptions column={1} bordered size="small">
                  {Object.entries(paymentDetails.webhookData).map(
                    ([key, value]) => (
                      <Descriptions.Item label={key} key={key}>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </Descriptions.Item>
                    ),
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Allocations */}
            {paymentDetails.allocations &&
              paymentDetails.allocations.length > 0 && (
                <>
                  <Divider orientation="left">Payment Allocations</Divider>
                  <Table
                    columns={allocationColumns}
                    dataSource={paymentDetails.allocations}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </>
              )}

            {/* Validation Info */}
            {paymentDetails.validatedBy && (
              <Card title="Validation Information" size="small">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Validated By">
                    {paymentDetails.validatedBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Validated At">
                    {paymentDetails.validatedAt
                      ? dayjs(paymentDetails.validatedAt).format(
                          "DD MMM YYYY HH:mm",
                        )
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Space>
        )
      )}
    </Drawer>
  );
};

export default PaymentDetailsDrawer;
